/**
 * OpenTelemetry Browser Instrumentation for React Admin SPA
 * Tracks user interactions, API calls, and performance metrics
 */

import { diag, DiagConsoleLogger, DiagLogLevel, trace } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { onINP } from 'web-vitals';

// Configuration from environment variables
const OTEL_ENDPOINT = import.meta.env.VITE_OTEL_ENDPOINT || 'http://localhost:4318/v1/traces';
const SERVICE_NAME = import.meta.env.VITE_OTEL_SERVICE_NAME || 'client-web-admin';
const SERVICE_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
const OTEL_ENABLED = import.meta.env.VITE_OTEL_ENABLED !== 'false';
const SAMPLE_RATE = parseFloat(import.meta.env.VITE_OTEL_SAMPLE_RATE || '0.1');
const IS_PRODUCTION = import.meta.env.PROD;

let isInitialized = false;
let tracer: ReturnType<typeof trace.getTracer>;

/**
 * Initialize OpenTelemetry for browser
 */
export function initializeOTel() {
  if (!OTEL_ENABLED || isInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    // Set up diagnostic logging (only in development)
    if (!IS_PRODUCTION) {
      diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.INFO);
    }

    // Create resource with service information
    const resource = new Resource({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      [ATTR_SERVICE_VERSION]: SERVICE_VERSION
    });

    // Create OTLP exporter
    const exporter = new OTLPTraceExporter({
      url: OTEL_ENDPOINT,
      headers: {}
    });

    // Create batch span processor
    const spanProcessor = new BatchSpanProcessor(exporter, {
      maxQueueSize: 100,
      maxExportBatchSize: 10,
      scheduledDelayMillis: 5000
    });

    // Create tracer provider with resource
    const provider = new WebTracerProvider({
      resource: resource,
      sampler: {
        shouldSample: () => {
          if (Math.random() <= SAMPLE_RATE) {
            return { decision: 2 }; // RECORD_AND_SAMPLED
          }
          return { decision: 0 }; // NOT_RECORD
        },
        toString: () => `ProbabilitySampler{${SAMPLE_RATE}}`
      }
    });

    // sdk-trace-web@1.x requires registering the span processor explicitly.
    // The `spanProcessors` constructor option is v2-only and was silently ignored
    // on 1.28 — so the exporter was never attached and admin traces never sent.
    (
      provider as unknown as { addSpanProcessor: (sp: typeof spanProcessor) => void }
    ).addSpanProcessor(spanProcessor);

    // Register the provider
    provider.register();

    // Register instrumentations
    registerInstrumentations({
      instrumentations: [
        // Track fetch requests
        new FetchInstrumentation({
          ignoreUrls: [/localhost:4318/, /assets\//],
          propagateTraceHeaderCorsUrls: [
            /^https:\/\/api\.carofi\.app/,
            /^https:\/\/telemetry\.carofi\.app/,
            /^https:\/\/internal-admin-env-prod\.carofi\.app/
          ],
          clearTimingResources: true
        }),

        // Track XMLHttpRequest
        new XMLHttpRequestInstrumentation({
          ignoreUrls: [/localhost:4318/, /assets\//],
          propagateTraceHeaderCorsUrls: [
            /^https:\/\/api\.carofi\.app/,
            /^https:\/\/telemetry\.carofi\.app/,
            /^https:\/\/internal-admin-env-prod\.carofi\.app/
          ]
        }),

        // Track document load
        new DocumentLoadInstrumentation(),

        // Track user interactions
        new UserInteractionInstrumentation({
          eventNames: ['click', 'submit']
        })
      ]
    });

    tracer = trace.getTracer(SERVICE_NAME);
    isInitialized = true;

    // Capture Core Web Vitals
    captureWebVitals();

    if (!IS_PRODUCTION) {
      console.log('✅ OpenTelemetry initialized for', SERVICE_NAME);
    }
  } catch (error) {
    console.error('❌ Failed to initialize OpenTelemetry:', error);
  }
}

/**
 * Capture Core Web Vitals
 */
function captureWebVitals() {
  if (!tracer || typeof window === 'undefined') return;

  if ('PerformanceObserver' in window) {
    // LCP
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1] as any;

        const span = tracer.startSpan('web-vitals.lcp');
        span.setAttribute('metric.name', 'lcp');
        span.setAttribute('metric.value', lastEntry.renderTime || lastEntry.loadTime);
        span.setAttribute(
          'metric.rating',
          getRating('lcp', lastEntry.renderTime || lastEntry.loadTime)
        );
        span.end();
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // Not supported
    }

    // INP - Interaction to Next Paint
    // Replaces FID (deprecated, removed from Core Web Vitals in March 2024). INP
    // measures the full latency of all interactions, not just the first input's
    // delay, so it must come from the web-vitals library, which aggregates event
    // timing across the page lifecycle and reports the worst interaction.
    try {
      onINP((metric) => {
        const span = tracer.startSpan('web-vitals.inp');
        span.setAttribute('metric.name', 'inp');
        span.setAttribute('metric.value', metric.value);
        span.setAttribute('metric.rating', getRating('inp', metric.value));
        span.end();
      });
    } catch (e) {
      // Not supported
    }

    // CLS
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((entryList) => {
        entryList.getEntries().forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      window.addEventListener('beforeunload', () => {
        const span = tracer.startSpan('web-vitals.cls');
        span.setAttribute('metric.name', 'cls');
        span.setAttribute('metric.value', clsValue);
        span.setAttribute('metric.rating', getRating('cls', clsValue));
        span.end();
      });
    } catch (e) {
      // Not supported
    }

    // TTFB
    try {
      const navigationEntry = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming;
      if (navigationEntry) {
        const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
        const span = tracer.startSpan('web-vitals.ttfb');
        span.setAttribute('metric.name', 'ttfb');
        span.setAttribute('metric.value', ttfb);
        span.setAttribute('metric.rating', getRating('ttfb', ttfb));
        span.end();
      }
    } catch (e) {
      // Not supported
    }
  }
}

function getRating(metric: string, value: number): string {
  const thresholds: Record<string, { good: number; needsImprovement: number }> = {
    lcp: { good: 2500, needsImprovement: 4000 },
    inp: { good: 200, needsImprovement: 500 },
    cls: { good: 0.1, needsImprovement: 0.25 },
    ttfb: { good: 800, needsImprovement: 1800 }
  };

  const threshold = thresholds[metric];
  if (!threshold) return 'unknown';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

export function trackPageView(pagePath: string, attributes?: Record<string, any>) {
  if (!tracer) return;

  const span = tracer.startSpan('page.view');
  span.setAttribute('page.path', pagePath);
  span.setAttribute('page.url', window.location.href);

  // Add any additional attributes
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, value);
    });
  }

  span.end();
}

export function setUserContext(userId: string, userEmail?: string) {
  if (!tracer) return;

  const span = tracer.startSpan('user.context');
  span.setAttribute('user.id', userId);
  if (userEmail) {
    span.setAttribute('user.email', userEmail);
  }
  span.end();
}

export function trackUserAction(action: string, attributes?: Record<string, any>) {
  if (!tracer) return;

  const span = tracer.startSpan(`user.action.${action}`);
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, value);
    });
  }
  span.end();
}

/**
 * Track an error
 */
export function trackError(error: Error, context?: Record<string, any>) {
  if (!tracer) return;

  const span = tracer.startSpan('error');
  span.setAttribute('error.type', error.name);
  span.setAttribute('error.message', error.message);
  if (error.stack) {
    span.setAttribute('error.stack', error.stack);
  }
  if (context) {
    Object.entries(context).forEach(([key, value]) => {
      span.setAttribute(`error.context.${key}`, value);
    });
  }
  span.end();
}

export default {
  initializeOTel,
  trackPageView,
  setUserContext,
  trackUserAction,
  trackError
};
